import { useLayoutEffect, useRef, useState } from 'react';

/**
 * Lock a container's current rendered height (as `min-height`) while `locked` is true.
 *
 * Capture steps show an absolutely-positioned overlay (`inset: 0`) over the camera web
 * component, so the overlay's size comes entirely from the container — and the
 * container's height comes from the camera. When the app step advances, the camera
 * remounts (its render key includes the step) and has ~zero height until its video
 * stream initializes. The container collapses, the overlay (with its bottom-anchored
 * buttons at `bottom: 15%`) collapses with it, then jumps back to full size — users end
 * up chasing the Continue button. Locking the pre-transition height keeps the overlay
 * stable for the whole camera re-init window.
 *
 * The height is measured on the first render where `locked` becomes true, so callers
 * must flip `locked` while the old camera is still mounted (e.g. on PROCESSING, before
 * the step change remounts the camera). The measurement is kept (not re-taken) across
 * subsequent overlay sub-steps and released when `locked` goes false. A 0 measurement
 * (camera not yet sized, e.g. a page refresh straight into review) means "nothing to
 * lock" — same behavior as before this hook.
 */
export function useLockedMinHeight<T extends HTMLElement>(locked: boolean) {
  const ref = useRef<T | null>(null);
  const [minHeight, setMinHeight] = useState<null | number>(null);

  useLayoutEffect(() => {
    if (!locked) {
      setMinHeight(null);

      return;
    }

    setMinHeight((current) => {
      if (current !== null) {
        return current;
      }

      const measured = ref.current?.offsetHeight ?? 0;

      return measured > 0 ? measured : null;
    });
  }, [locked]);

  return { minHeight, ref };
}
